import { useContext } from '@berlin-front/common/composables/context';

import { computed, toRefs, reactive, unref, watch, Ref, ref } from '@vue/composition-api';
import { useMutation, useQuery } from 'vue-query';
import { Route, Location } from 'vue-router';

import { useRouter } from '@berlin-front/common/composables/useRouter';
import useOrAll from '@berlin-front/common/composables/useOrAll';
import useErrors from '@composables/useErrors';
import useFakeProgress from '@berlin-front/common/composables/useFakeProgress';
import { BerlinBffContext } from '@/services/BerlinBff';
import { ApiCacheContext } from '@/services/ApiCacheContext';
import { getRefDeep, getRequiredRefValueByPath } from '@berlin-front/common/utils';
import { ROUTE_NAME } from '@/router/route-name';
import { ATONE_SMS_DESCRIPTION_MESSAGE, AUTHCODE_RESEND_MESSAGE, UseSmsAuthContextType } from './context';

export const useSmsAuth = (_$route: Route): UseSmsAuthContextType => ({
  useSmsAuth: (authCodeRef: Ref) => {
    const router = useRouter();
    const {
      atoneModalParameter,
      atoneSmsNotifyPostResult,
      atoneAuthenticateGetResult,
      atoneShopV2OptionGetResult,
      atoneTracksPostResult,
      setAtoneTracksTrustResult,
      tokens,
      setAtonePointGetResult,
      setAtoneSmsNotifyPostResult,
      setPaymentMethodGetResult,
    } = useContext(ApiCacheContext);
    const { sms, point, tracks, payment } = useContext(BerlinBffContext);
    const nextPage = ref<Location>();
    const { errors, clearAll: clearErrorMessages } = useErrors({
      fieldMapper: {},
      defaultFieldName: 'flashMessages',
    });

    const errorMessages = computed(() => unref(errors)?.flashMessages ?? []);
    const infoMessages = ref<string[]>([]);

    const state = reactive({
      smsToken: '',
    });

    // 表示項目
    const phoneNumber = computed(
      () => atoneSmsNotifyPostResult.value?.phoneNumber || atoneAuthenticateGetResult.value?.signInId || '',
    );

    // SMS検証。4桁の数字が揃ったら呼び出す。(submit)
    const {
      isSuccess: isSuccessSmsVerify,
      isError: isErrorSmsVerify,
      mutate: mutateSmsVerify,
      reset: resetSmsVerify,
    } = useMutation(
      'atone.SmsVerifyPost',
      () =>
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        sms.atoneSmsControllerSmsVerifyPost(getRefDeep(tokens, 'npToken'), atoneModalParameter.value.pubKey, {
          trackToken: getRefDeep(atoneTracksPostResult, 'trackToken'),
          smsToken: state.smsToken,
        }),
      {
        retry: false,
      },
    );

    // フィンガープリント更新。SMS認証OKなら呼び出し。
    const { isSuccess: isSuccessTrackTokenUpdate, isError: isErrorTrackTokenUpdate } = useQuery(
      'atone.TrackTokenUpdate',
      () =>
        tracks.atoneTracksControllerTrackTokenUpdate(
          getRefDeep(atoneTracksPostResult, 'trackToken'),
          getRefDeep(tokens, 'npToken'),
          atoneModalParameter.value.pubKey,
          {
            trustedFlag: true,
          },
        ),
      {
        enabled: isSuccessSmsVerify,
        onSuccess: () => {
          // フィンガープリント更新に成功したら、Trackトークンをtrusted扱いに変更
          setAtoneTracksTrustResult({ trustedFlag: true });
        },
      },
    );

    // ポイント取得。フィンガープリント更新に成功したら呼び出し。
    const { isSuccess: isSuccessAtonePointGet, isError: isErrorAtonePointGet } = useQuery(
      'atone.atonePointGet',
      () =>
        point.atonePointControllerPointGet(
          getRefDeep(tokens, 'npToken'),
          atoneModalParameter.value.pubKey,
          getRefDeep(tokens, 'trustedTrackToken'),
        ),
      {
        enabled: isSuccessTrackTokenUpdate,
        onSuccess: ({ data: atonePointGetResult }) => {
          setAtonePointGetResult(atonePointGetResult);
        },
      },
    );

    const { isSuccess: isSuccessAtonePaymentControllerMethodGet, isError: isErrorAtonePaymentControllerMethodGet } =
      useQuery(
        'atone.atonePaymentControllerMethodGet',
        () =>
          payment.atonePaymentControllerMethodGet(
            getRefDeep(tokens, 'npToken'),
            getRefDeep(tokens, 'trustedTrackToken'),
            getRequiredRefValueByPath({ atoneModalParameter }, 'pubKey'),
            getRequiredRefValueByPath({ atoneModalParameter }, 'payment').amount,
            atoneModalParameter.value?.payment?.transactionOptions,
          ),
        {
          enabled: isSuccessAtonePointGet,
          onSuccess: ({ data: atoneMethodGetResult }) => {
            setPaymentMethodGetResult(atoneMethodGetResult);
            if (atoneMethodGetResult.splitPayment) {
              // atoneプラス会員の場合
              // atoneプラス会員では分割払いの情報がレスポンスに含まれ、その情報を元にプラス会員用の画面に遷移する。
              if (
                !atoneShopV2OptionGetResult.value?.atoneSplitServiceOption.errors &&
                atoneMethodGetResult.splitPayment.usablePatterns
              ) {
                // 分割利用可能な場合は、支払い会異数の選択画面に遷移する。
                nextPage.value = { name: ROUTE_NAME.ATONE_SPLIT_SELECT };
              } else {
                // 分割利用不可の場合は、一回払いの画面に遷移し利用不可の理由を表示する。
                nextPage.value = { name: ROUTE_NAME.ATONE_SPLIT_SETTLEMENT_ONE_TIME };
              }
            } else {
              // 通常会員の場合は、通常の決済画面に遷移する。
              nextPage.value = { name: ROUTE_NAME.ATONE_SETTLEMENT_CONFIRM };
            }
          },
        },
      );

    // SMS送信。再送ボタンが押されたら呼び出す。
    const {
      isSuccess: isSuccessSmsNotifyPost,
      isError: isErrorSmsNotifyPost,
      mutate: mutateSmsResend,
      reset: resetSmsResend,
    } = useMutation(
      'atone.SmsNotifyPost',
      () =>
        sms.atoneSmsControllerSmsNotifyPost(getRefDeep(tokens, 'npToken'), atoneModalParameter.value.pubKey, {
          trackToken: getRefDeep(atoneTracksPostResult, 'trackToken'),
          resend: true,
        }),
      {
        onSuccess: ({ data }) => {
          setAtoneSmsNotifyPostResult(data);
          infoMessages.value = [AUTHCODE_RESEND_MESSAGE];
        },
      },
    );

    const isLoading = ref(false);
    const isError = useOrAll(
      isErrorSmsVerify,
      isErrorTrackTokenUpdate,
      isErrorAtonePointGet,
      isErrorSmsNotifyPost,
      isErrorAtonePaymentControllerMethodGet,
    );
    const isSuccess = useOrAll(isSuccessAtonePaymentControllerMethodGet, isSuccessSmsNotifyPost);

    // 成功、または失敗になったらisEnded
    const isEnded = useOrAll(isSuccess, isError);

    // 進捗。useMutationの状況に合わせてプログレスを進める。
    const {
      progress,
      completed: progressCompleted,
      isLoading: isLoadingApi,
    } = toRefs(useFakeProgress(5000, isLoading, isEnded));

    const clearInfoMessages = () => {
      infoMessages.value = [];
    };

    // そのままmutateをキックするのではなく、
    // - 実行中でないことを確認
    // - 実行後（正常系）でないことを確認
    // した上でリクエストを行う。（バリデーションはAuthCodeInput内で確認済）
    const onSubmitVerify = (token: string) => {
      state.smsToken = token;
      if (!unref(isLoading) && !unref(isSuccessAtonePaymentControllerMethodGet)) {
        clearErrorMessages();
        clearInfoMessages();
        isLoading.value = true;
        mutateSmsVerify();
      }
    };

    const onSubmitResend = () => {
      if (!unref(isLoading)) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        authCodeRef.value.clearNumbers(); // 認証コード入力フォームをクリア
        clearErrorMessages();
        clearInfoMessages();
        isLoading.value = true;
        mutateSmsResend();
      }
    };

    watch(progressCompleted, (result) => {
      if (result) {
        if (unref(isSuccessAtonePaymentControllerMethodGet) && nextPage.value) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          router.replace(nextPage.value); // 認証成功なら画面遷移
          return;
        }
        isLoading.value = false;
        resetSmsResend.value?.();
        resetSmsVerify.value?.();
      }
    });

    // エラーが発生したら認証コードを消去する
    watch(isError, () => {
      if (isError.value) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        authCodeRef.value.clearNumbers(); // 認証コード入力フォームをクリア
      }
    });

    return reactive({
      // 表示項目
      description: ATONE_SMS_DESCRIPTION_MESSAGE,
      phoneNumber,
      errorMessages,
      infoMessages,

      // progress
      progress,

      // loading
      isLoadingApi,

      // 関数
      onSubmitVerify,
      onSubmitResend,

      backToRegister: undefined,
      backToPrevious: undefined,
    });
  },
});
